import { useState, useCallback, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import ShareIcon from '@mui/icons-material/IosShare';

type Props = {
  title: string;
  path: string;
  className?: string;
  buttonComponent?: React.ElementType;
  children?: ReactNode;
  tooltip?: string;
  [key: string]: unknown;
};

const ShareButton = ({ title, path, className, buttonComponent: ButtonComponent = Button, children, tooltip, ...rest }: Props) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const url = `https://everydayrides.com${path}`;

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setSnackbarOpen(true);
    } catch {
      // Clipboard API not available
    }
  }, [title, url]);

  const button = (
    <ButtonComponent
      onClick={handleShare}
      className={className}
      {...(children ? {} : { startIcon: <ShareIcon /> })}
      {...rest}
    >
      {children ?? 'Share'}
    </ButtonComponent>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Link copied to clipboard"
      />
    </>
  );
};

export default ShareButton;
